import React, { useMemo } from 'react';

import LoadingMessage from '@components/LoadingMessage';

import useExperimentPlotGroupsV3 from '@hooks/useExperimentPlotGroupsV3';

import Experiment from '@models/Experiment';
import Plot from '@models/Plot';

import { compareGroupNames, formatStringToNumberWithSeparator } from '@util/StringUtil';

type Props = {
    experiment: Experiment;
    plot: Plot;
    title?: string;
    annotation_set_id?: string;
    latent_variable_id?: string;
    variable_ids?: string[];
};
const PreviewGroups = ({
    experiment,
    plot,
    annotation_set_id = '',
    latent_variable_id = '',
    variable_ids = [],
    title = 'Preview groups',
}: Props) => {
    const { loading: groupsLoading, groups } = useExperimentPlotGroupsV3({
        annotation_set_id,
        experiment,
        latent_variable_id,
        plot,
        variable_ids,
        shouldFetchOnChange: true,
    });

    const noSelectionsMade = !annotation_set_id && !latent_variable_id && !variable_ids.length;

    const filteredGroups = useMemo(
        () =>
            (groups ?? [])
                .filter((g) => g.cell_count > 0)
                .sort((g1, g2) => compareGroupNames(g1.display_name, g2.display_name))
                .map((g) => ({
                    id: g.uuid,
                    display_name: g.display_name,
                    sample_count: g.sample_count,
                    cell_count: g.cell_count,
                })),
        [groups],
    );

    const legendItems = useMemo(() => {
        const orderedItemIds = filteredGroups?.map((g) => g.id) ?? [];
        return orderedItemIds.map((groupId) => filteredGroups?.find((g) => g.id === groupId)).filter(Boolean);
    }, [filteredGroups]);

    return (
        <div className="mb-8 flex flex-col">
            <h4 className="mb-2 text-lg font-semibold tracking-tight text-dark">{title}</h4>
            <div className="space-y-1">
                {groupsLoading ? (
                    <LoadingMessage inline immediate message="Loading..." />
                ) : noSelectionsMade ? (
                    <p>
                        Select one or more variables to summarize your cells into groups, and the resulting groups will
                        be shown as a preview below. After running the analysis, you&apos;ll be able to explore these
                        groups in the Plot tab and adjust group visibility and appearance.
                    </p>
                ) : legendItems.length === 0 && !groupsLoading ? (
                    <p>No groups found</p>
                ) : (
                    legendItems.map((group) => (
                        <p key={group?.id}>
                            {group?.display_name} ({formatStringToNumberWithSeparator(group?.cell_count || '')} cells)
                        </p>
                    ))
                )}
            </div>
        </div>
    );
};

export default PreviewGroups;
